import { useState, useEffect, useCallback } from "react";
import { getLoggedinUser } from "../../../helpers/api_helper";
import { createAdmin } from "../../../api/auth/authService";
import { updateUser } from "../../../api/accessManagement/usersService";
import type { UserFormData, AddUserDrawerMode } from "./AddUserDrawer";

interface UseAddUserDrawerParams {
  show: boolean;
  onHide: () => void;
  mode: AddUserDrawerMode;
  initialData?: UserFormData;
  onSuccess?: () => void;
}

export function useAddUserDrawer({
  show,
  onHide,
  mode,
  initialData,
  onSuccess,
}: UseAddUserDrawerParams) {
  const isEdit = mode === "edit" && initialData;
  const isView = mode === "view" && initialData;

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState<number | undefined>(undefined);
  const [initialScope, setInitialScope] = useState("");
  // Start with no role selected for create; edit/view will set from initialData
  const [role, setRole] = useState("");
  const [scopeType, setScopeType] = useState("");
  const [scopeIds, setScopeIds] = useState<string[]>([]);
  const [academyId, setAcademyId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSubmitError(null);
      setPhoneError(null);
      if ((isEdit || isView) && initialData) {
        setFullName(initialData.name);
        setEmail(initialData.email);
        setPhone(initialData.phone ?? "");
        setPassword("");
        setCountryCode(initialData.countryCode);
        setInitialScope(initialData.initialScope ?? "");
        // For edit/view, prefill with the user's first role if present
        setRole(initialData.roles[0] ?? "");
        setScopeType(initialData.scopeType ?? "");
        setScopeIds(initialData.scopeIds ?? (initialData.scopeId ? [initialData.scopeId] : []));
        setAcademyId("");
      } else {
        setFullName("");
        setEmail("");
        setPhone("");
        setPassword("");
        setCountryCode(undefined);
        setInitialScope("");
        // For create, require explicit role selection
        setRole("");
        setScopeType("");
        setScopeIds([]);
        setAcademyId("");
      }
    }
  }, [show, isEdit, isView, initialData]);

  const handleFullNameChange = useCallback((v: string) => setFullName(v), []);
  const handleEmailChange = useCallback((v: string) => setEmail(v), []);
  const handlePhoneChange = useCallback((v: string) => {
    setPhone(v);
    setPhoneError(null);
  }, []);
  const handlePasswordChange = useCallback((v: string) => setPassword(v), []);
  const handleCountryCodeChange = useCallback((v: number | undefined) => setCountryCode(v), []);
  const handleInitialScopeChange = useCallback((v: string) => setInitialScope(v), []);
  const handleRoleChange = useCallback((v: string) => setRole(v), []);
  const handleScopeTypeChange = useCallback((v: string) => {
    setScopeType(v);
    setAcademyId("");
    setScopeIds([]);
  }, []);
  const handleScopeIdsChange = useCallback((ids: string[]) => setScopeIds(ids), []);
  const handleAcademyIdChange = useCallback((v: string) => {
    setAcademyId(v);
    setScopeIds([]);
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    setPhoneError(null);

    if (!fullName.trim()) {
      setSubmitError("Full name is required");
      return;
    }

    if (!email.trim()) {
      setSubmitError("Email is required");
      return;
    }

    const phoneDigits = phone.replace(/\D/g, "");
    const phoneNumber = phoneDigits ? parseInt(phoneDigits, 10) : undefined;
    if (phoneDigits && Number.isNaN(phoneNumber)) {
      setPhoneError("Invalid phone number");
      return;
    }
    if (phoneDigits && phoneDigits.length !== 10) {
      setPhoneError("Phone number must be 10 digits");
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEdit && initialData?.userId) {
        // Edit existing user: only user details are updated
        await updateUser(initialData.userId, {
          fullName: fullName.trim(),
          email: email.trim(),
          countryCode: countryCode ?? undefined,
          phoneNumber,
          password: password || undefined,
        });
      } else {
        // Create new user (existing flow)
        const currentUser = getLoggedinUser();
        const createdBy =
          currentUser?.id ??
          currentUser?._id ??
          currentUser?.user_id ??
          currentUser?.userId ??
          undefined;

        if (!password || password.length < 8) {
          setSubmitError("Password is required and must be at least 8 characters");
          return;
        }

        if (!role) {
          setSubmitError("Role is required");
          return;
        }

        if (scopeType.trim() && scopeIds.length === 0) {
          setSubmitError("Select at least one Scope ID when Scope Type is set");
          return;
        }

        await createAdmin({
          fullName: fullName.trim(),
          email: email.trim(),
          password,
          countryCode: countryCode ?? undefined,
          phoneNumber,
          role_code: role,
          scope_type: scopeType.trim() || undefined,
          scope_ids: scopeIds.length > 0 ? scopeIds : undefined,
          created_by: createdBy,
        });
      }

      onSuccess?.();
      onHide();
    } catch (err: unknown) {
      let message = isEdit ? "Failed to update user" : "Failed to create user";

      if (err && typeof err === "object") {
        const anyErr = err as any;
        const respData = anyErr.response?.data;

        if (respData && typeof respData === "object") {
          message =
            (respData.error as string | undefined) ||
            (respData.message as string | undefined) ||
            message;
        } else if ("message" in anyErr && typeof anyErr.message === "string") {
          message = anyErr.message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isEdit,
    initialData,
    fullName,
    email,
    password,
    phone,
    countryCode,
    role,
    scopeType,
    scopeIds,
    onHide,
    onSuccess,
  ]);

  const drawerTitle = isView ? "View User" : isEdit ? "Edit User" : "Add User";

  return {
    functions: {
      handleFullNameChange,
      handleEmailChange,
      handlePhoneChange,
      handlePasswordChange,
      handleCountryCodeChange,
      handleInitialScopeChange,
      handleRoleChange,
      handleScopeTypeChange,
      handleScopeIdsChange,
      handleAcademyIdChange,
      handleSubmit,
    },
    values: {
      fullName,
      email,
      phone,
      password,
      countryCode,
      initialScope,
      role,
      scopeType,
      scopeIds,
      academyId,
      isEdit,
      isView,
      drawerTitle,
      submitError,
      phoneError,
    },
    loading: { isSubmitting },
  };
}
